'use client';

import clsx from 'clsx';
import { type JSX, useEffect, useState, type ForwardedRef } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconCheckCircle, IconInfoCircle, IconWarning, IconWarningCircleFill } from '../../icons/index.js';
import { withStyleProps } from '../../with-style-props.js';
import { XButton } from '../internal/x-button/x-button.js';
import type { CommonProps, IconProps, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

const iconMap: Record<AlertProps['tone'], (props: IconProps) => JSX.Element> = {
  info: IconInfoCircle,
  success: IconCheckCircle,
  warning: IconWarning,
  critical: IconWarningCircleFill,
};

export interface AlertProps extends CommonProps {
  /** The title of the alert. */
  title?: string;
  /** The content of the alert. */
  children: StringLikeChildren;
  /** The tone of the alert. */
  tone: 'info' | 'success' | 'warning' | 'critical';
  /**
   * Whether the alert can be dismissed by the user.
   * @default false
   */
  isDismissible?: boolean;
  /** Callback function to be called when the dismiss button is pressed. Makes the alert controlled. */
  onDismiss?: () => void;
}

function Alert(
  { UNSAFE_className, UNSAFE_style, tone, title, children, isDismissible = false, onDismiss, ...props }: AlertProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const Icon = iconMap[tone];
  const messages = useLocalizedMessages('common');

  const [isDismissed, setIsDismissed] = useState(false);
  function handleDismiss() {
    if (onDismiss == null) {
      setIsDismissed(true);
    }
    onDismiss?.();
  }

  // This is required for NVDA announcements, without it NVDA will NOT announce the alert when it appears.
  // See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/alert_role#:~:text=alerts%20will%20only%20be%20announced%20when
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);

  if (isDismissed) {
    return null;
  }

  return (
    <div {...props} ref={ref} className={clsx('cim-alert', UNSAFE_className)} style={UNSAFE_style} data-tone={tone}>
      <div className="cim-alert-icon">
        <Icon size={24} aria-hidden />
      </div>
      <div role="alert" className="cim-alert-content">
        {/* aria-hidden must be set on the inner div, otherwise it breaks for VoiceOver */}
        <div aria-hidden={isVisible ? undefined : 'true'}>
          {title && (
            <Text as="h3" UNSAFE_className="cim-alert-title" variant="title-6">
              {title}
            </Text>
          )}
          <Text as="p" UNSAFE_className="cim-alert-text" variant="medium">
            {children}
          </Text>
        </div>
      </div>
      {(isDismissible || onDismiss != null) && (
        <div className="cim-alert-close">
          <XButton aria-label={messages.format('dismiss')} onPress={handleDismiss} />
        </div>
      )}
    </div>
  );
}

/**
 * Displays an inline message banner in response to a user action.
 *
 * See [alert usage guidelines](https://ui.cimpress.io/components/alert/).
 */
const _Alert = withStyleProps(forwardRef(Alert), 'Alert');

export { _Alert as Alert };
